import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  FileSpreadsheet,
  FileText,
  FolderSearch,
  Image as ImageIcon,
  Loader2,
  RotateCcw,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import {
  submissionDocumentsApi,
  type ReportSubmissionDocumentDto,
} from "@/api/reportsV32";
import { ApiError } from "@/api/client";
import { useSubmissionDocuments } from "@/hooks/reports/useSubmissionDocuments";
import { DocumentUploadDropzone } from "./DocumentUploadDropzone";
import { RepositoryDocumentPicker } from "./RepositoryDocumentPicker";
import { TagDropdown } from "./TagDropdown";
import { cn } from "@/design-system/lib/utils";

// LLM & Reports v3.2 §8.1 step 4 — orchestrator over Stage 1's
// DocumentUploadDropzone + TagDropdown. Owns the per-submission
// upload state, fans out attach / detach / retag / retry mutations,
// and renders the row list with status indicators (Pending /
// Succeeded / Failed / Skipped).
//
// The master document, if present, is auto-tagged "Master" and
// pinned to the top with a gold accent. Its tag dropdown is
// disabled and the delete affordance is hidden — to remove the
// master, the user goes back to Step 3 (Master Document) and
// changes the template's master selection.

interface SubmissionDocumentsSectionProps {
  submissionId: string;
  /**
   * Target DMS folder for new uploads. Required by the backend
   * UploadAsync contract — the parent picks a sensible default
   * (e.g., the user's reports folder) so the user doesn't have
   * to navigate the DMS tree.
   */
  folderId: string;
  /**
   * Locks the section to read-only — used post-generation when the
   * report is already Drafted and we don't want users editing the
   * attachments out from under the generated artifact.
   */
  locked?: boolean;
  className?: string;
}

const MAX_FILE_SIZE_MB = 32;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_ATTACHMENTS = 50;

const ACCEPT_HINT = [
  ".docx",
  ".xlsx",
  ".csv",
  ".pdf",
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".txt",
  ".md",
].join(",");

export function SubmissionDocumentsSection({
  submissionId,
  folderId,
  locked = false,
  className,
}: SubmissionDocumentsSectionProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const list = useSubmissionDocuments(submissionId);
  const [browseOpen, setBrowseOpen] = useState(false);

  const invalidateList = () =>
    queryClient.invalidateQueries({
      queryKey: ["submission-documents", submissionId],
    });

  const attachMutation = useMutation({
    mutationFn: (file: File) =>
      submissionDocumentsApi.attach(submissionId, {
        file,
        folderId,
        tagCode: "Other",
      }),
    onSuccess: (row) => {
      invalidateList();
      // Surface skipped / failed status as a toast so users notice;
      // Succeeded uploads pop a quieter confirmation.
      if (row.conversionStatus === "Failed") {
        toast({
          tone: "error",
          message: `Couldn't ingest ${row.fileName}: ${row.conversionError ?? "see status"}.`,
        });
      } else if (row.conversionStatus === "Skipped") {
        toast({
          tone: "info",
          message: `${row.fileName} uploaded but not ingested (${row.conversionError ?? "unsupported format"}).`,
        });
      } else {
        toast({ tone: "success", message: `${row.fileName} ready.` });
      }
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : "Upload failed.";
      toast({ tone: "error", message: msg });
    },
  });

  const updateTagMutation = useMutation({
    mutationFn: ({ rowId, tagCode }: { rowId: string; tagCode: string | null }) =>
      submissionDocumentsApi.update(submissionId, rowId, { tagCode }),
    onSuccess: () => invalidateList(),
    onError: (err) => {
      const msg = err instanceof Error ? err.message : "Tag update failed.";
      toast({ tone: "error", message: msg });
    },
  });

  const detachMutation = useMutation({
    mutationFn: (rowId: string) => submissionDocumentsApi.detach(submissionId, rowId),
    onSuccess: () => {
      invalidateList();
      toast({ tone: "success", message: "Attachment removed." });
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : "Could not remove attachment.";
      toast({ tone: "error", message: msg });
    },
  });

  // v3.6 — pull existing repository documents in by reference (no
  // re-upload). The backend runs reports ingestion per document so each
  // attached row gets a conversion record; idempotent on re-import.
  const attachByRefMutation = useMutation({
    mutationFn: (documentIds: string[]) =>
      submissionDocumentsApi.attachByReference(submissionId, documentIds, "Other"),
    onSuccess: (rows) => {
      invalidateList();
      toast({
        tone: "success",
        message: `Added ${rows.length} document${rows.length === 1 ? "" : "s"} to the report.`,
      });
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : "Could not add documents.";
      toast({ tone: "error", message: msg });
    },
  });

  const retryMutation = useMutation({
    mutationFn: (rowId: string) => submissionDocumentsApi.retry(submissionId, rowId),
    onSuccess: (row) => {
      invalidateList();
      if (row.conversionStatus === "Succeeded") {
        toast({ tone: "success", message: `${row.fileName} converted on retry.` });
      } else {
        toast({
          tone: "info",
          message: `${row.fileName} still ${row.conversionStatus}: ${row.conversionError ?? "(no detail)"}.`,
        });
      }
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : "Retry failed.";
      toast({ tone: "error", message: msg });
    },
  });

  const handleFiles = (files: File[]) => {
    if (locked) return;
    const existing = list.data?.length ?? 0;
    if (existing + files.length > MAX_ATTACHMENTS) {
      toast({
        tone: "error",
        message: `${MAX_ATTACHMENTS}-attachment limit reached. Remove some before adding more.`,
      });
      return;
    }
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast({
          tone: "error",
          message: `${file.name} is over ${MAX_FILE_SIZE_MB} MB. Compress or split it before uploading.`,
        });
        continue;
      }
      attachMutation.mutate(file);
    }
  };

  const rows = list.data ?? [];
  const sorted = [...rows].sort((a, b) => {
    const aMaster = a.tagCode === "Master" ? 0 : 1;
    const bMaster = b.tagCode === "Master" ? 0 : 1;
    if (aMaster !== bMaster) return aMaster - bMaster;
    return a.displayOrder - b.displayOrder;
  });

  return (
    <div className={cn("space-y-space-3", className)}>
      <DocumentUploadDropzone
        onFiles={handleFiles}
        disabled={locked || attachMutation.isPending}
        accept={ACCEPT_HINT}
        hint={
          locked
            ? "Attachments are locked while the report is being generated"
            : "Drop files here or click to browse — Word, Excel, PDF, or images"
        }
      />

      {!locked && (
        <div className="flex items-center gap-space-2">
          <span className="font-body text-caption text-gray-mid">or</span>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setBrowseOpen(true)}
            disabled={attachByRefMutation.isPending}
          >
            <FolderSearch size={14} aria-hidden /> Browse repository
          </Button>
          <span className="font-body text-caption text-gray-mid">
            Pull in a document that already lives in the repository.
          </span>
        </div>
      )}

      <RepositoryDocumentPicker
        open={browseOpen}
        onClose={() => setBrowseOpen(false)}
        attachedDocumentIds={(list.data ?? []).map((r) => r.documentId)}
        remainingSlots={Math.max(0, MAX_ATTACHMENTS - (list.data?.length ?? 0))}
        adding={attachByRefMutation.isPending}
        onAdd={async (documentIds) => {
          await attachByRefMutation.mutateAsync(documentIds);
          setBrowseOpen(false);
        }}
      />

      {list.isLoading && (
        <p className="font-body text-caption text-gray-mid">Loading attachments…</p>
      )}
      {list.isError && (
        <p className="font-body text-caption text-status-error">
          Couldn't load attachments. Refresh the page or try again in a moment.
        </p>
      )}

      {sorted.length > 0 && (
        <ul className="divide-y divide-border-light rounded-md border border-border-light">
          {sorted.map((row) => (
            <DocumentRow
              key={row.id}
              row={row}
              locked={locked}
              onTagChange={(tagCode) =>
                updateTagMutation.mutate({ rowId: row.id, tagCode })
              }
              onDelete={() => detachMutation.mutate(row.id)}
              onRetry={() => retryMutation.mutate(row.id)}
              tagUpdating={
                updateTagMutation.isPending && updateTagMutation.variables?.rowId === row.id
              }
              retryPending={
                retryMutation.isPending && retryMutation.variables === row.id
              }
              deletePending={
                detachMutation.isPending && detachMutation.variables === row.id
              }
            />
          ))}
        </ul>
      )}

      {!list.isLoading && sorted.length === 0 && (
        <p className="font-body text-caption text-gray-mid">
          No attachments yet. Drop a file above to add one.
        </p>
      )}
    </div>
  );
}

interface DocumentRowProps {
  row: ReportSubmissionDocumentDto;
  locked: boolean;
  onTagChange: (tagCode: string | null) => void;
  onDelete: () => void;
  onRetry: () => void;
  tagUpdating: boolean;
  retryPending: boolean;
  deletePending: boolean;
}

function DocumentRow({
  row,
  locked,
  onTagChange,
  onDelete,
  onRetry,
  tagUpdating,
  retryPending,
  deletePending,
}: DocumentRowProps) {
  const isMaster = row.tagCode === "Master";
  const failed = row.conversionStatus === "Failed";
  const skipped = row.conversionStatus === "Skipped";
  const succeeded = row.conversionStatus === "Succeeded";
  const pending = row.conversionStatus === "Pending";

  return (
    <li
      className={cn(
        "flex items-center gap-space-3 px-space-3 py-space-2",
        isMaster && "bg-gold-highlight/10 border-l-4 border-l-gold",
        failed && "bg-status-error/5",
      )}
    >
      <FileTypeIcon contentType={row.contentType} className="text-gold-shadow shrink-0" />

      <div className="min-w-0 flex-1">
        <p className="font-body text-body text-onyx truncate">
          {row.fileName}
          {isMaster && (
            <span className="ml-space-2 font-display text-caption uppercase tracking-[0.1em] text-gold-shadow">
              Master
            </span>
          )}
        </p>
        <p className="font-body text-caption text-gray-mid">
          {formatBytes(row.fileSizeBytes)}
          {row.estimatedInputTokens > 0 && (
            <> • ~{row.estimatedInputTokens.toLocaleString()} tokens</>
          )}
        </p>
        {failed && row.conversionError && (
          <p className="font-body text-caption text-status-error mt-space-1" role="alert">
            {row.conversionError}
          </p>
        )}
        {skipped && row.conversionError && (
          <p className="font-body text-caption text-gray-mid mt-space-1">
            {row.conversionError}
          </p>
        )}
      </div>

      <StatusPill
        status={row.conversionStatus}
        pending={pending}
        succeeded={succeeded}
        failed={failed}
        skipped={skipped}
      />

      <div className="w-48 shrink-0">
        <TagDropdown
          value={row.tagCode ?? null}
          onChange={onTagChange}
          disabled={isMaster || locked || tagUpdating}
          allowMaster={isMaster}
          ariaLabel={`Tag for ${row.fileName}`}
        />
      </div>

      <div className="flex items-center gap-space-1 shrink-0">
        {failed && !locked && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onRetry}
            disabled={retryPending}
            aria-label={`Retry ingesting ${row.fileName}`}
          >
            <RotateCcw size={14} aria-hidden /> Retry
          </Button>
        )}
        {!isMaster && !locked && (
          <button
            type="button"
            onClick={onDelete}
            disabled={deletePending}
            aria-label={`Remove ${row.fileName}`}
            className={cn(
              "rounded-md p-space-1",
              "text-gray-mid hover:text-status-error hover:bg-status-error/10",
              "disabled:opacity-50 disabled:pointer-events-none",
            )}
          >
            {deletePending ? (
              <Loader2 size={16} className="animate-spin" aria-hidden />
            ) : (
              <X size={16} aria-hidden />
            )}
          </button>
        )}
        {isMaster && (
          <span
            className="text-gray-mid"
            title="The master document is set on the template; change it from Step 3."
          >
            <Trash2 size={16} aria-hidden className="opacity-30" />
          </span>
        )}
      </div>
    </li>
  );
}

function StatusPill({
  status,
  pending,
  succeeded,
  failed,
  skipped,
}: {
  status: string;
  pending: boolean;
  succeeded: boolean;
  failed: boolean;
  skipped: boolean;
}) {
  if (succeeded) {
    return (
      <span
        className="inline-flex items-center gap-space-1 text-status-success"
        aria-label="Conversion succeeded"
      >
        <CheckCircle2 size={14} aria-hidden /> Ready
      </span>
    );
  }
  if (failed) {
    return (
      <span
        className="inline-flex items-center gap-space-1 text-status-error"
        aria-label="Conversion failed"
      >
        <AlertTriangle size={14} aria-hidden /> Failed
      </span>
    );
  }
  if (skipped) {
    return (
      <span
        className="inline-flex items-center gap-space-1 text-gray-mid"
        aria-label="Conversion skipped"
      >
        <AlertTriangle size={14} aria-hidden /> Skipped
      </span>
    );
  }
  if (pending) {
    return (
      <span
        className="inline-flex items-center gap-space-1 text-gold-shadow"
        aria-label="Conversion pending"
      >
        <Loader2 size={14} className="animate-spin" aria-hidden /> Converting
      </span>
    );
  }
  return <span className="text-caption text-gray-mid">{status}</span>;
}

function FileTypeIcon({
  contentType,
  className,
}: {
  contentType: string;
  className?: string;
}) {
  if (contentType.startsWith("image/")) {
    return <ImageIcon size={20} aria-hidden className={className} />;
  }
  if (
    contentType.includes("spreadsheetml") ||
    contentType === "text/csv" ||
    contentType === "application/vnd.ms-excel"
  ) {
    return <FileSpreadsheet size={20} aria-hidden className={className} />;
  }
  return <FileText size={20} aria-hidden className={className} />;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Re-export ApiError so callers can narrow on it without re-importing.
export { ApiError as SubmissionDocumentsApiError };
